<template>
  <CardWithTitle
    :item-id="itemId"
    :map-keys="mapKeys"
    :request-status="requestStatus"
    :options-data="cardOptions"
    :has-data-to-export="hasDataToExport"
    class="make-a-copy"
    @call-option-menu="callOptionMenu"
    @reload="getData"
  >
    <v-data-table
      v-if="hasItems"
      :headers="headers"
      :items="items"
      :items-per-page="itemsPerPage"
      :loading="inputLoading"
      class="table-truncate"
    >
      <template v-slot:[`item.queue_time`]="{ item }">
        <span>{{ item.queueTime }}</span>
      </template>
      <template v-slot:[`item.customer_name`]="{ item }">
        <span>{{ item.customerName }}</span>
      </template>
      <template v-slot:[`item.team`]="{ item }">
        <span>{{ item.team }}</span>
      </template>
      <template v-slot:[`item.sequential_id`]="{ item }">
        <span>{{ item.sequentialId }}</span>
      </template>
      <template v-slot:[`item.priority`]="{ item }">
        <span>{{ item.priority }}</span>
      </template>
      <template v-slot:[`item.bot_name`]="{ item }">
        <span>{{ item.botName }}</span>
      </template>
    </v-data-table>

    <template #top-right-slot>
      <Badge
        v-if="hasFilterApplied && isEditor"
        type="info"
        :tooltip="$t('components.Badge.label.tooltip.info')"
        :value="$t('components.Badge.label.icon.key')"
        :preset="true"
      />
    </template>
  </CardWithTitle>
</template>

<script>
import CardWithTitle from '@/components/cards/models/CardWithTitle';
import blipModel from '@/mixins/blip-model';
import { mapActions } from 'vuex';
import { timeToSeconds, secondsToTime } from '@/utils/formatter';
import exportModel from '@/mixins/export-model';
import cardsModel from '@/mixins/cards-model';
import Badge from '@/components/Badge';

export default {
  name: 'BlipWaitingTicketsTable',
  components: {
    CardWithTitle,
    Badge,
  },
  mixins: [
    exportModel,
    cardsModel,
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    hasDataToExport: {
      type: Boolean,
      default: true,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      headers: [
        { text: this.$t('components.BlipWaitingTicketsTable.queue_time'), value: 'queueTime' },
        { text: this.$t('components.BlipWaitingTicketsTable.customer_name'), value: 'customerName' },
        { text: this.$t('components.BlipWaitingTicketsTable.team'), value: 'team' },
        { text: this.$t('components.BlipWaitingTicketsTable.sequential_id'), value: 'sequentialId' },
        { text: this.$t('components.BlipWaitingTicketsTable.priority'), value: 'priority' },
        { text: this.$t('components.BlipWaitingTicketsTable.bot_name'), value: 'botName' },
      ],
      rawSeries: [],
      cardFilters: {},
      dialog: false,
      itemsPerPage: 5,
      requestStatus: 'loading',
      mapKeys: {
        queueTime: this.$t('components.BlipWaitingTicketsTable.queue_time'),
        customerName: this.$t('components.BlipWaitingTicketsTable.customer_name'),
        team: this.$t('components.BlipWaitingTicketsTable.team'),
        sequentialId: this.$t('components.BlipWaitingTicketsTable.sequential_id'),
        priority: this.$t('components.BlipWaitingTicketsTable.priority'),
        botName: this.$t('components.BlipWaitingTicketsTable.bot_name'),
      },
    };
  },
  computed: {
    inputLoading() {
      return this.requestStatus === 'loading';
    },
    hasItems() {
      return Boolean(this.items.length);
    },
    cardOptions() {
      if (this.hasDataToExport) {
        return this.baseCardOptions.concat(this.exportWithXLSXOptionsData(this.items)).sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
      }
      return this.baseCardOptions;
    },
    items() {
      return this.rawSeries.flatMap((serie) => {
        const resourceItems = serie.resource?.items.map((item) => {
          return {
            botName: this.getBotName(serie.to, this.cardFilters),
            customerName: item?.customerName,
            priority: item?.priority,
            queueTime: secondsToTime(timeToSeconds(item?.queueTime)),
            sequentialId: `#${item?.sequentialId}`,
            team: item?.team,
          };
        }) || [];

        return resourceItems;
      });
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
      'getAppliedCardFilter',
    ]),
    exportWithXLSXOptionsData(items) {
      const exportExtensionOptions = this.exportExtensions.map((extension, index) => (
        {
          order: index + 1,
          name: extension.type,
          function: () => (this.exportComponent(extension, true)),
        }
      ));
      exportExtensionOptions.push({
        order: exportExtensionOptions.length + 1,
        name: 'XLSX',
        function: () => (this.exportComponentXLSX(items)),
      });
      return [
        {
          order: 5,
          name: this.$t('mixins.exportModel.label.exportLabel'),
          iconname: 'download',
          function: () => ([]),
          child: exportExtensionOptions,
          trackName: 'ST CardMenu Export Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
          isExpandedModeOption: true,
        },
      ];
    },
    async getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        return;
      }

      this.cardFilters = await this.getAppliedCardFilter(this.itemId);

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then((responses) => {
          if (!options?.silentRefresh) {
            this.requestStatus = 'loading';
          }

          if (this.totalResponseItems(responses)) {
            this.requestStatus = 'emptyData';
            return;
          }

          this.rawSeries = responses;
          this.requestStatus = 'success';
        })
        .catch(error => {
          console.error(error);
          this.items = [];
          this.requestStatus = error.message;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.v-select__selection.v-select__selection--comma {
  line-height: 120%;
}

.buttons {
  display: grid;
}

.clean-sla-buttons {
  box-shadow: none;
  border-radius: unset;
}

.close-button {
  display: flex;
  flex: end;
  justify-content: flex-end;
}

::v-deep {
  .table-truncate {
    td {
      max-width: 10vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .modal-style {
    * {
      color: var(--ds-color-snow) !important;
    }

    h3 {
      font-weight: var(--ds-font-weight-bold);
    }
  }
}
</style>
